import type { ReactNode } from 'react'
import React, { createContext, useContext, useState, useEffect, useRef } from 'react'
import { useHistory, useLocation } from 'react-router-dom'

interface LocationTrackerContextType {
  history: string[]
  updateHistory: (path: string) => void
  removeLastEntry: () => void
}

const LocationTrackerContext = createContext<LocationTrackerContextType | undefined>(undefined)

interface LocationTrackerProviderProps {
  children: ReactNode
}

const LocationTrackerProvider: React.FC<LocationTrackerProviderProps> = ({ children }) => {
  const [history, setHistory] = useState<string[]>([])
  const location = useLocation()
  const routerHistory = useHistory()
  // The previous location, recorded or not — a bare '/settings' is never recorded, so replacing it
  // must not drop the entry before it.
  const previousPath = useRef<string>()

  useEffect(() => {
    const fullPath = `${location.pathname}${location.search}`
    const replacesRecordedEntry = routerHistory.action === 'REPLACE' && previousPath.current !== '/settings'
    previousPath.current = fullPath
    updateHistory(fullPath, replacesRecordedEntry)
  }, [location])

  const updateHistory = (path: string, replace = false) => {
    setHistory((prevHistory) => {
      if (prevHistory.at(-1) === path || path === '/settings') {
        return prevHistory
      }
      // A replace removes the current entry from the browser's stack, so it must leave this list too.
      // Otherwise Back returns to the replaced URL: saving a new template replaces
      // /quotes/new/template/:type with /quotes/edit/:id, and Back reopened the create dialog (ENG-2434).
      if (replace) {
        return [...prevHistory.slice(0, -1), path]
      }
      const lastPathname = prevHistory.at(-1)?.split('?')[0]
      const newPathname = path.split('?')[0]
      if (lastPathname === newPathname) {
        return [...prevHistory.slice(0, -1), path]
      }
      return [...prevHistory, path]
    })
  }

  const removeLastEntry = () => {
    setHistory((prevHistory) => prevHistory.slice(0, -1))
  }

  return (
    <LocationTrackerContext.Provider value={{ history, updateHistory, removeLastEntry }}>
      {children}
    </LocationTrackerContext.Provider>
  )
}

export function useLocationTracker(): LocationTrackerContextType {
  const context = useContext(LocationTrackerContext)
  if (context === undefined) {
    throw new Error('useLocationTracker must be used within a LocationTrackerProvider')
  }
  return context
}

export default LocationTrackerProvider
